// src/quiz/gre-mock5/verbalData.js
// GRE Mock 5 — Verbal Reasoning (hardest set so far).
// Section 1: questions 1–12 (18 min). Section 2: questions 13–27 (23 min).
// Question format: see ../greQuestionTypes.js

const SE_INSTRUCTION = "Select the two answer choices that, when used to complete the sentence, fit the meaning of the sentence as a whole and produce completed sentences that are alike in meaning.";

const COMMONS_PASSAGE =
  "In 1968 the ecologist Garrett Hardin argued that any resource held in common—a pasture, a fishery, the atmosphere—is doomed to overuse, because each user captures the full benefit of additional exploitation while bearing only a fraction of its cost. Hardin's conclusion, that commons must be either privatized or placed under state control, proved enormously influential among policymakers.\n\n" +
  "The political scientist Elinor Ostrom did not deny the logic of Hardin's model; she denied its realism. Hardin's herders, she observed, were imagined as strangers incapable of communicating, whereas actual communities sharing resources—Swiss alpine villages, Japanese forest cooperatives, Spanish irrigation districts—had in many cases sustained them for centuries by devising their own rules for access, monitoring, and sanctions. Ostrom's fieldwork did not show that communal management always succeeds; she documented many failures. What it showed was that the choice between privatization and state control is a false dichotomy, and that the success of self-governance depends on identifiable conditions, such as clearly defined boundaries and the ability of users to take part in modifying the rules.";

const PRINT_PASSAGE =
  "Traditional histories of the printing press emphasize its role in spreading the ideas of the Protestant Reformation. Yet print served the established Church as well: indulgences—certificates remitting punishment for sin—were among the earliest documents printed in Europe, and they were produced in vast quantities. The technology, in short, was available to reformers and authorities alike; what distinguished the reformers was not access to presses but their skill in exploiting the new medium's capacity for brief, inexpensive pamphlets written in the vernacular.";

const DRIFT_PASSAGE =
  "When Alfred Wegener proposed in 1912 that the continents had once formed a single landmass and had since drifted apart, he marshaled an impressive range of evidence: the jigsaw fit of the Atlantic coastlines, matching fossil species on continents now separated by oceans, and rock formations that seemed to continue from one continent to another. Yet for nearly half a century most geologists rejected his hypothesis.\n\n" +
  "It is tempting to attribute this rejection to professional conservatism or to Wegener's status as an outsider—he was trained in meteorology, not geology. But such explanations neglect a substantive objection. Wegener could offer no plausible mechanism capable of moving continents through the solid ocean floor, and the forces he did propose were shown by physicists to be far too weak. For many of his critics, a hypothesis that described an effect without a credible cause was not yet science. When evidence of seafloor spreading emerged in the 1950s and 1960s, it supplied precisely what Wegener had lacked—not more evidence that the continents had moved, but an account of how they could—and the theory of plate tectonics won acceptance within a decade.";

const THEFT_PASSAGE =
  "In the city of Corvan, the number of reported bicycle thefts fell by 30 percent in the year after the city installed surveillance cameras at its major bicycle racks. The city council concludes that the cameras deterred would-be thieves.";

const CAFE_PASSAGE =
  "Last year the wholesale price of coffee beans rose sharply. Café Lumen, which sells only coffee beverages, did not raise its prices and sold about the same number of beverages as in the previous year. Nevertheless, its profits increased.";

const verbalQuestions = [

  // ---------- SECTION 1 ----------

  {
    id: 1,
    question: "The senator's speeches, once celebrated for their ______, had become so laden with qualifications and digressions that even sympathetic listeners lost the thread.",
    options: ["perspicuity", "prolixity", "sententiousness", "acrimony", "diffidence"],
    answer: "perspicuity",
    explanation: "\"Once celebrated\" contrasts with speeches now so cluttered that listeners lose the thread, so they were once praised for clarity: perspicuity. Prolixity (wordiness) describes what they became.",
  },
  {
    id: 2,
    type: "blanks",
    question: "Although the treatise is often dismissed as (i)______, a careful reading reveals that its seemingly random digressions are (ii)______ to its central argument.",
    blanks: [
      { options: ["desultory", "rigorous", "polemical"] },
      { options: ["integral", "inimical", "tangential"] },
    ],
    answer: ["desultory", "integral"],
    explanation: "\"Seemingly random digressions\" is why readers dismiss it as desultory (aimless, unmethodical). \"Although\" signals that careful reading shows the opposite: the digressions are integral to the argument. Tangential would agree with the dismissal.",
  },
  {
    id: 3,
    type: "blanks",
    question: "The archive's new director was determined to (i)______ the institution's reputation for (ii)______; under her predecessor, researchers had waited months for documents that, once produced, often proved to have been (iii)______.",
    blanks: [
      { options: ["dispel", "burnish", "exploit"] },
      { options: ["inefficiency", "openness", "scholarship"] },
      { options: ["misfiled", "digitized", "annotated"] },
    ],
    answer: ["dispel", "inefficiency", "misfiled"],
    explanation: "Months-long waits for documents that turn out to have been misfiled describe inefficiency. A new director would want to dispel (get rid of) such a reputation, not burnish or exploit it.",
  },
  {
    id: 4,
    type: "blanks",
    question: "Far from (i)______ the theory, the anomalous results were, on closer inspection, exactly what it (ii)______.",
    blanks: [
      { options: ["undermining", "confirming", "popularizing"] },
      { options: ["predicted", "precluded", "ignored"] },
    ],
    answer: ["undermining", "predicted"],
    explanation: "Anomalous results would seem to undermine a theory. \"Far from\" reverses that: on closer inspection the results were exactly what the theory predicted.",
  },
  {
    id: 5,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The negotiator's ______ manner concealed a shrewd intelligence; opponents who mistook her mildness for weakness invariably regretted it.",
    options: ["unassuming", "self-effacing", "imperious", "truculent", "flamboyant", "ostentatious"],
    answer: ["unassuming", "self-effacing"],
    explanation: "Her manner showed \"mildness\" that could be mistaken for weakness: unassuming and self-effacing. Imperious, truculent, flamboyant and ostentatious are all forceful or showy.",
  },
  {
    id: 6,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Given the ______ of evidence linking the two events, the historian refused to speculate about a causal connection between them.",
    options: ["paucity", "dearth", "abundance", "plethora", "quality", "provenance"],
    answer: ["paucity", "dearth"],
    explanation: "\"Given\" means the evidence explains her refusal to speculate, so there was little of it: paucity and dearth. Abundance and plethora are a pair, but they would make the refusal surprising, which needs \"despite,\" not \"given.\"",
  },
  {
    id: 7,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Reviewers ______ the memoir for its self-serving omissions, noting that the author glossed over every episode that reflected poorly on him.",
    options: ["excoriated", "castigated", "lauded", "extolled", "scrutinized", "anthologized"],
    answer: ["excoriated", "castigated"],
    explanation: "\"Self-serving omissions\" is a criticism, so the reviewers attacked the memoir: excoriated and castigated (both mean harshly criticized). Lauded and extolled form a pair of opposite meaning.",
  },
  {
    id: 8,
    passage: COMMONS_PASSAGE,
    question: "The primary purpose of the passage is to",
    options: [
      "describe a challenge to the assumptions underlying an influential argument",
      "argue that communal management of resources is always superior to privatization",
      "defend Hardin's conclusion against its critics",
      "compare the merits of privatization and state control",
      "catalog the failures of communal resource management",
    ],
    answer: "describe a challenge to the assumptions underlying an influential argument",
    explanation: "The first paragraph presents Hardin's influential argument; the second presents Ostrom's challenge to its realism (its assumptions), not its logic. B overstates: Ostrom \"documented many failures.\"",
  },
  {
    id: 9,
    type: "multi",
    passage: COMMONS_PASSAGE,
    question: "According to the passage, Ostrom's objections to Hardin's model include which of the following?",
    options: [
      "It assumes that resource users cannot communicate with one another.",
      "It overlooks communities that have managed shared resources sustainably for long periods.",
      "Its internal logic is flawed.",
    ],
    answer: [
      "It assumes that resource users cannot communicate with one another.",
      "It overlooks communities that have managed shared resources sustainably for long periods.",
    ],
    explanation: "Hardin's herders were \"imagined as strangers incapable of communicating\" (A), whereas real communities \"sustained them for centuries\" (B). Ostrom \"did not deny the logic of Hardin's model,\" so C is wrong.",
  },
  {
    id: 10,
    passage: COMMONS_PASSAGE,
    question: "It can be inferred that Ostrom would most likely agree that a fishing community would be more likely to sustain its fishery if",
    options: [
      "its members could take part in revising the rules governing the fishery",
      "the fishery were transferred to a single private owner",
      "rules for the fishery were imposed by an outside authority",
      "the boundaries of the fishery were left deliberately vague",
      "its members had never met one another",
    ],
    answer: "its members could take part in revising the rules governing the fishery",
    explanation: "The passage names \"the ability of users to take part in modifying the rules\" as a condition for successful self-governance. D contradicts the other named condition, clearly defined boundaries.",
  },
  {
    id: 11,
    passage: PRINT_PASSAGE,
    question: "The author mentions indulgences primarily in order to",
    options: [
      "show that the printing press was not used only to advance the Reformation",
      "explain why the Reformation succeeded",
      "argue that the Church opposed the printing press",
      "illustrate the high cost of early printed documents",
      "suggest that reformers lacked access to printing presses",
    ],
    answer: "show that the printing press was not used only to advance the Reformation",
    explanation: "Indulgences are introduced with \"Yet print served the established Church as well,\" countering histories that stress print's role in spreading Reformation ideas.",
  },
  {
    id: 12,
    passage: PRINT_PASSAGE,
    question: "Which of the following, if true, would most strengthen the author's claim about what distinguished the reformers?",
    options: [
      "Church authorities of the period printed mainly lengthy Latin treatises, whereas reformers printed mostly short pamphlets in local languages.",
      "Printing presses were far more numerous in regions sympathetic to the Reformation.",
      "Indulgences had been sold for centuries before the printing press was invented.",
      "Many leading reformers had been trained as priests.",
      "The price of paper fell steadily during the sixteenth century.",
    ],
    answer: "Church authorities of the period printed mainly lengthy Latin treatises, whereas reformers printed mostly short pamphlets in local languages.",
    explanation: "The author says the difference lay in how reformers used print (brief, cheap, vernacular pamphlets), not access. A shows exactly that contrast. B points to a difference in access, which would weaken the claim.",
  },

  // ---------- SECTION 2 ----------

  {
    id: 13,
    question: "The biographer's portrait is admirably ______: it neither excuses the statesman's failings nor allows them to eclipse his considerable achievements.",
    options: ["evenhanded", "hagiographic", "censorious", "desultory", "tendentious"],
    answer: "evenhanded",
    explanation: "Neither excusing failings nor letting them overshadow achievements is balanced: evenhanded. Hagiographic (excessively flattering) and censorious (harshly critical) are the two extremes the sentence rules out.",
  },
  {
    id: 14,
    question: "The committee's report, for all its length, was strikingly ______: it catalogued problems in exhaustive detail but offered not a single recommendation.",
    options: ["noncommittal", "partisan", "succinct", "incendiary", "exhaustive"],
    answer: "noncommittal",
    explanation: "A report that offers no recommendation takes no position: noncommittal. \"For all its length\" rules out succinct, and exhaustive describes the detail, which is not what is striking.",
  },
  {
    id: 15,
    type: "blanks",
    question: "The theory's (i)______ was also its weakness: by accounting for every conceivable outcome, it ruled out none and was thus effectively (ii)______.",
    blanks: [
      { options: ["comprehensiveness", "parsimony", "novelty"] },
      { options: ["unfalsifiable", "verifiable", "obsolete"] },
    ],
    answer: ["comprehensiveness", "unfalsifiable"],
    explanation: "Accounting for every outcome is comprehensiveness, which sounds like a strength. But a theory that rules out nothing cannot be shown wrong by any result: it is unfalsifiable.",
  },
  {
    id: 16,
    type: "blanks",
    question: "The playwright's early comedies, with their (i)______ plots and stock characters, gave little hint of the (ii)______ she would later bring to her tragedies, works whose moral seriousness has (iii)______ comparison with the greatest of her contemporaries.",
    blanks: [
      { options: ["formulaic", "labyrinthine", "austere"] },
      { options: ["gravity", "levity", "haste"] },
      { options: ["invited", "precluded", "deflected"] },
    ],
    answer: ["formulaic", "gravity", "invited"],
    explanation: "Plots paired with \"stock characters\" are formulaic. They gave little hint of the later gravity (seriousness), echoed by \"moral seriousness.\" Work of such quality has invited comparison with the greatest writers.",
  },
  {
    id: 17,
    type: "blanks",
    question: "Many species once thought to be (i)______ to the island have since been found elsewhere, a discovery that has (ii)______ conservationists' estimates of how many unique species the island harbors.",
    blanks: [
      { options: ["endemic", "inimical", "invasive"] },
      { options: ["reduced", "bolstered", "corroborated"] },
    ],
    answer: ["endemic", "reduced"],
    explanation: "Species found only in one place are endemic to it. If they turn up elsewhere, they are no longer unique to the island, so the count of unique species is reduced.",
  },
  {
    id: 18,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The professor's lectures, though ______, rewarded patient listeners with insights unavailable elsewhere.",
    options: ["tedious", "soporific", "riveting", "incisive", "unoriginal", "ephemeral"],
    answer: ["tedious", "soporific"],
    explanation: "\"Though\" and \"patient listeners\" suggest the lectures were hard to sit through: tedious and soporific (sleep-inducing). Unoriginal contradicts \"insights unavailable elsewhere.\"",
  },
  {
    id: 19,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Critics charged that the agency's reforms were merely ______, designed to placate the public without altering any of its underlying practices.",
    options: ["cosmetic", "superficial", "sweeping", "radical", "belated", "punitive"],
    answer: ["cosmetic", "superficial"],
    explanation: "Reforms that change nothing underneath are only on the surface: cosmetic and superficial. Sweeping and radical are a pair with the opposite meaning.",
  },
  {
    id: 20,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "The ______ heir squandered in a single decade a fortune that three generations had accumulated.",
    options: ["profligate", "prodigal", "parsimonious", "frugal", "reclusive", "litigious"],
    answer: ["profligate", "prodigal"],
    explanation: "Someone who squanders a fortune is recklessly wasteful: profligate and prodigal. Parsimonious and frugal are the opposite pair.",
  },
  {
    id: 21,
    type: "multi",
    selectCount: 2,
    instruction: SE_INSTRUCTION,
    question: "Her refusal to compromise, which admirers called principled, struck her detractors as merely ______.",
    options: ["obdurate", "intransigent", "pragmatic", "conciliatory", "capricious", "venal"],
    answer: ["obdurate", "intransigent"],
    explanation: "Detractors give the same refusal to compromise a negative label: stubborn, i.e. obdurate and intransigent. Conciliatory and pragmatic suggest willingness to compromise.",
  },
  {
    id: 22,
    passage: DRIFT_PASSAGE,
    question: "Which of the following best expresses the main idea of the passage?",
    options: [
      "The long rejection of Wegener's hypothesis reflected a substantive scientific objection rather than mere prejudice.",
      "Wegener's hypothesis was rejected chiefly because he was not trained as a geologist.",
      "Wegener's evidence that the continents had moved was too weak to persuade geologists.",
      "Physicists played a larger role than geologists in the development of plate tectonics.",
      "Scientific hypotheses are generally accepted only after their originators have died.",
    ],
    answer: "The long rejection of Wegener's hypothesis reflected a substantive scientific objection rather than mere prejudice.",
    explanation: "The author sets aside conservatism and outsider status as explanations and argues that the real issue was the lack of a credible mechanism. C is wrong because the passage calls his evidence \"impressive\"; the missing piece was the cause.",
  },
  {
    id: 23,
    passage: DRIFT_PASSAGE,
    question: "The author mentions Wegener's training in meteorology primarily in order to",
    options: [
      "acknowledge a factor sometimes cited to explain the rejection of his hypothesis",
      "suggest that meteorology provided the mechanism Wegener needed",
      "argue that outsiders are better placed to make scientific discoveries",
      "explain why physicists objected to his proposed forces",
      "show that Wegener's evidence was unreliable",
    ],
    answer: "acknowledge a factor sometimes cited to explain the rejection of his hypothesis",
    explanation: "His meteorology training explains his \"status as an outsider,\" one of the tempting explanations the author raises and then says is insufficient.",
  },
  {
    id: 24,
    type: "multi",
    passage: DRIFT_PASSAGE,
    question: "The passage suggests which of the following about the evidence of seafloor spreading?",
    options: [
      "It addressed a shortcoming that had been central to critics' objections.",
      "It consisted mainly of additional fossil matches between continents.",
      "It contributed to the relatively rapid acceptance of plate tectonics.",
    ],
    answer: [
      "It addressed a shortcoming that had been central to critics' objections.",
      "It contributed to the relatively rapid acceptance of plate tectonics.",
    ],
    explanation: "It \"supplied precisely what Wegener had lacked,\" a mechanism (A), and plate tectonics \"won acceptance within a decade\" (C). It was \"not more evidence that the continents had moved,\" so B is wrong.",
  },
  {
    id: 25,
    passage: DRIFT_PASSAGE,
    question: "Which of the following, if true, would most seriously undermine the author's explanation of why Wegener's hypothesis was rejected?",
    options: [
      "Many geologists who rejected Wegener's hypothesis readily accepted other theories of the period for which no causal mechanism had been proposed.",
      "Later research confirmed that the fossil species Wegener cited are found on several continents.",
      "A few geologists accepted Wegener's hypothesis well before the 1950s.",
      "The physicists' calculations of the forces Wegener proposed were accurate.",
      "Wegener first published his hypothesis in German.",
    ],
    answer: "Many geologists who rejected Wegener's hypothesis readily accepted other theories of the period for which no causal mechanism had been proposed.",
    explanation: "The author claims critics rejected drift because it lacked a mechanism. If the same critics happily accepted other mechanism-free theories, that principle cannot explain their rejection, and prejudice becomes more plausible. D supports the author.",
  },
  {
    id: 26,
    passage: THEFT_PASSAGE,
    question: "Which of the following would be most useful to know in evaluating the council's conclusion?",
    options: [
      "Whether reported bicycle thefts at locations without cameras also declined during that year",
      "How much the city paid to install the cameras",
      "Whether the cameras record in color or in black and white",
      "How many bicycles are registered in Corvan",
      "Whether the council plans to install cameras at additional locations",
    ],
    answer: "Whether reported bicycle thefts at locations without cameras also declined during that year",
    explanation: "If thefts fell just as much where there were no cameras, something else (such as a citywide trend) probably caused the drop, and the cameras deserve no credit. If they did not fall elsewhere, the conclusion is strengthened. None of the other facts bears on cause.",
  },
  {
    id: 27,
    passage: CAFE_PASSAGE,
    question: "Which of the following, if true, most helps to resolve the apparent discrepancy described above?",
    options: [
      "Early last year, Café Lumen switched to a supplier whose prices, under a long-term contract, were substantially lower than those Café Lumen had previously paid.",
      "Café Lumen's rent increased last year.",
      "Wholesale coffee prices are expected to fall next year.",
      "Many other cafés raised their prices last year.",
      "Café Lumen's employees received pay raises last year.",
    ],
    answer: "Early last year, Café Lumen switched to a supplier whose prices, under a long-term contract, were substantially lower than those Café Lumen had previously paid.",
    explanation: "With the same prices and the same sales volume, higher profits require lower costs. A explains how Café Lumen's bean costs fell even though wholesale prices rose. B and E raise costs, which deepens the puzzle.",
  },
];

export default verbalQuestions;
